/**
 * Estimated Comms Degradation layer (Ice Storm Risk → "Estimated Comms
 * Degradation") — the render of the #109 fusion-derived `comms_degradation_zones`.
 * The estimated cell-availability-loss picture: where a sustained power-outage
 * cluster (cell sites run ~2–8 h on battery, then go dark) — corroborated by a
 * freezing-rain CAP and the #108 telecom ASN tripwire — makes comms PROBABLY
 * unreachable (operator-cop-doctrine §2.1 comms/reach-back, IMS §2).
 *
 * HONEST FRAMING is the whole point: this is an ESTIMATE, not measured coverage.
 * Per §3B.8 it lives in the AMBIENT register — a faint, GROUND-CLAMPED translucent
 * indigo MultiPolygon (the comms family's own hue, distinct from outage red / road
 * bronze / flood fuchsia), coloured by estimate CONFIDENCE, NEVER pulsing, with a
 * small "Est. comms loss" centroid label so the estimate reads as an estimate on
 * the map itself. The polygon is the click target → the CommsDegradationZoneBody
 * panel, which lists the "probably unreachable" communities.
 *
 * Render gate (build.md § L.2, the road-closures/power-outages precedent): HIDDEN
 * at country zoom, visible regional+. Imperative installer (MapView owns the
 * viewer), mirroring RoadClosuresLayer.
 */
import * as Cesium from 'cesium';
import type {
  CommsDegradationZoneFeature,
  CommsDegradationZoneFeatureCollection,
} from '@tsr/shared-types';
import { fetchCommsDegradationZones, type BboxQuery } from '../api';
import {
  commsZoneStyle,
  COMMS_ZONE_FILL_ALPHA,
  COMMS_ZONE_OUTLINE_ALPHA,
  COMMS_ZONE_OUTLINE_WIDTH,
} from '../styles/commsStyles';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { zoneGlyphBillboard, ZONE_GLYPH, ZONE_GLYPH_SIZE } from './zoneGlyph';

const REFRESH_DEBOUNCE_MS = 300;
const ZONE_PREFIX = 'comms-zone:';
const ZONE_LABEL_PREFIX = 'comms-zone-label:';
const ZONE_GLYPH_PREFIX = 'comms-zone-glyph:';

export interface CommsDegradationZonesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<CommsDegradationZoneFeatureCollection>;
}

export interface CommsDegradationZonesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its zone feature (null if not ours). */
  pick(entity: unknown): CommsDegradationZoneFeature | null;
  destroy(): void;
}

/** GeoJSON MultiPolygon → Cesium PolygonHierarchy[] (one per polygon, with holes). */
function multiPolygonHierarchies(coords: number[][][][] | undefined): Cesium.PolygonHierarchy[] {
  if (!coords) return [];
  const out: Cesium.PolygonHierarchy[] = [];
  for (const polygon of coords) {
    const rings = polygon;
    if (!rings || rings.length === 0) continue;
    const outer = rings[0];
    if (!outer || outer.length < 3) continue;
    const positions = Cesium.Cartesian3.fromDegreesArray(outer.flat());
    const holes = rings
      .slice(1)
      .filter((r) => r && r.length >= 3)
      .map((r) => new Cesium.PolygonHierarchy(Cesium.Cartesian3.fromDegreesArray(r.flat())));
    out.push(new Cesium.PolygonHierarchy(positions, holes));
  }
  return out;
}

export function installCommsDegradationZonesLayer(
  viewer: Cesium.Viewer,
  opts: CommsDegradationZonesLayerOptions = {},
): CommsDegradationZonesLayerHandle {
  const fetcher = opts.fetcher ?? fetchCommsDegradationZones;
  const ds = new Cesium.CustomDataSource('comms-degradation-zones');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the L.2 render gate).
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const entityToFeature = new Map<Cesium.Entity, CommsDegradationZoneFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (fc: CommsDegradationZoneFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      const style = commsZoneStyle(feature.properties.estimate_confidence);
      const color = Cesium.Color.fromCssColorString(style.color);
      const hierarchies = multiPolygonHierarchies(
        feature.geometry?.coordinates as number[][][][] | undefined,
      );
      // GROUND-CLAMPED translucent estimate polygon(s) — faint fill + outline,
      // never pulses. classificationType TERRAIN drapes them on relief.
      for (let i = 0; i < hierarchies.length; i++) {
        const poly = ds.entities.add({
          id: `${ZONE_PREFIX}${feature.id}:${i}`,
          polygon: {
            hierarchy: hierarchies[i],
            material: color.withAlpha(COMMS_ZONE_FILL_ALPHA),
            outline: true,
            outlineColor: color.withAlpha(COMMS_ZONE_OUTLINE_ALPHA),
            outlineWidth: COMMS_ZONE_OUTLINE_WIDTH,
            classificationType: Cesium.ClassificationType.TERRAIN,
            distanceDisplayCondition: gate,
          },
        });
        entityToFeature.set(poly, feature);
      }

      const [clon, clat] = feature.centroid;
      const centroid = Cesium.Cartesian3.fromDegrees(clon, clat);

      // #475 — the centred COMMS-TOWER IDENTITY glyph (the CAHS mast on the act-chip
      // plate with a thin confidence-coloured rim): SHAPE-primary "this coloured area
      // is estimated comms degradation", floated CLEAR of every zone fill (#473).
      // Canvas-free, so it renders in unit tests; undefined only if the glyph is unknown.
      const glyph = zoneGlyphBillboard(ZONE_GLYPH.comms_degradation, { rimColor: style.color });
      if (glyph) {
        const identity = ds.entities.add({
          id: `${ZONE_GLYPH_PREFIX}${feature.id}`,
          position: centroid,
          billboard: glyph,
        });
        entityToFeature.set(identity, feature);
      }

      // "Est. comms loss" centroid label — the estimate reads AS an estimate on the
      // map (honest framing is the whole point). Paired hue + word "Est." (colour
      // never load-bearing alone). Demoted BELOW the identity glyph (#475) so they
      // don't overlap: anchored at its TOP, offset down past the glyph.
      const label = ds.entities.add({
        id: `${ZONE_LABEL_PREFIX}${feature.id}`,
        position: centroid,
        label: {
          text: `Est. comms loss · ${style.label}`,
          font: '600 12px sans-serif',
          fillColor: color,
          outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.TOP,
          pixelOffset: new Cesium.Cartesian2(0, ZONE_GLYPH_SIZE * 0.6),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(label, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchCommsDegradationZones failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): CommsDegradationZoneFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
